import {
  BellOutlined,
  CalendarOutlined,
  EditOutlined,
  MailOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { Button, DatePicker, Divider, Modal, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useState } from "react";
import { openNotification } from "../../../../Components/Notification/Index";
import { NotificationReminder, User } from "../../../../Interface/Index";
import notificationReminderService from "../../../../Services/notification.reminder.service";
import { useCurrentApplication } from "../../../../Context/CurrentApplicationContext/CurrentApplicationContext";

interface ReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  assessmentExpiry: Date;
  user: User;
  jobApplicationId: string;
  sessionId: string;
  mode: "create" | "edit";
  existingReminder?: NotificationReminder | null;
  onSuccess?: (reminder: NotificationReminder) => void;
}

const ReminderModal: React.FC<ReminderModalProps> = ({
  isOpen,
  onClose,
  assessmentExpiry,
  user,
  jobApplicationId,
  sessionId,
  mode,
  existingReminder,
  onSuccess,
}) => {
  const [reminderDate, setReminderDate] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(false);
  const { userProfile } = useCurrentApplication();

  useEffect(() => {
    if (mode === "edit" && existingReminder?.scheduleAt) {
      setReminderDate(dayjs(existingReminder.scheduleAt));
    } else {
      setReminderDate(null);
    }
  }, [mode, existingReminder, isOpen]);

  if (!isOpen) return null;

  const expiryDayjs = dayjs(assessmentExpiry);
  const today = dayjs().startOf("day");

  const formattedExpiry = expiryDayjs.format("D MMM YYYY, hh:mm A");

  const disabledDate = (current: Dayjs) => {
    return current.isBefore(today) || !current.isBefore(expiryDayjs, "day");
  };

  const handleSubmit = async () => {
    if (!reminderDate) return;
    if (reminderDate.isBefore(dayjs())) {
      openNotification(
        "warning",
        "Invalid Time",
        "Please select a future date and time.",
      );
      return;
    }
    setLoading(true);
    try {
      let result: NotificationReminder | null = null;

      if (mode === "edit" && existingReminder?._id) {
        result = await notificationReminderService.updateNotificationReminder(
          existingReminder._id,
          { scheduleAt: reminderDate.toISOString() },
        );
      } else {
        result = await notificationReminderService.createNotificationReminder({
          jobApplicationId,
          sessionId,
          type: "assessment_reminder",
          scheduleAt: reminderDate.toISOString(),
        });
      }

      if (result) {
        openNotification(
          "success",
          mode === "edit" ? "Reminder Updated" : "Reminder Set",
          `You'll be reminded on ${reminderDate.format("D MMM YYYY, hh:mm A")}.`,
        );
        onSuccess?.(result);
        onClose();
      } else {
        openNotification(
          "error",
          "Failed",
          "Could not save the reminder. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const title =
    mode === "edit" ? (
      <>
        <EditOutlined /> Edit Assessment Reminder
      </>
    ) : (
      <>
        <CalendarOutlined /> Set Assessment Reminder
      </>
    );

  const userEmail = userProfile?.basicDetails?.email || user.email || "NA";
  const userMobile =
    userProfile?.basicDetails?.phoneNumber || user.mobileNumber || "NA";

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      centered
      footer={null}
      closeIcon={false}
      maskClosable={false}
      width={560}
    >
      <section className="reminder-modal">
        <div className="reminder-modal-header">
          <Typography.Title level={5} style={{ margin: 0 }}>
            {title}
          </Typography.Title>
        </div>
        <Divider style={{ margin: 0 }} />

        <div className="reminder-modal-content">
          <div className="reminder-modal-description">
            <Typography.Text type="secondary">
              A reminder to complete this assessment will be sent at your
              specified time.
            </Typography.Text>
          </div>

          <div className="reminder-modal-info-list">
            <div className="reminder-modal-info-item">
              <div className="reminder-modal-info-icon reminder-modal-info-icon--expiry">
                <CalendarOutlined />
              </div>
              <div className="reminder-modal-info-body">
                <Typography.Text
                  type="secondary"
                  className="reminder-modal-info-label"
                >
                  Assessment expires on
                </Typography.Text>
                <Typography.Text strong className="reminder-modal-info-value">
                  {formattedExpiry}
                </Typography.Text>
              </div>
            </div>

            {user.mobileNumber && <Divider style={{ margin: "4px 0" }} />}

            <div className="reminder-modal-info-item">
              <div className="reminder-modal-info-icon reminder-modal-info-icon--email">
                <MailOutlined />
              </div>
              <div className="reminder-modal-info-body">
                <Typography.Text
                  type="secondary"
                  className="reminder-modal-info-label"
                >
                  Email
                </Typography.Text>
                <Typography.Text
                  strong
                  className="reminder-modal-info-value"
                  ellipsis
                >
                  {userEmail}
                </Typography.Text>
              </div>
            </div>

            {user.mobileNumber && (
              <div className="reminder-modal-info-item">
                <div className="reminder-modal-info-icon reminder-modal-info-icon--whatsapp">
                  <WhatsAppOutlined />
                </div>
                <div className="reminder-modal-info-body">
                  <Typography.Text
                    type="secondary"
                    className="reminder-modal-info-label"
                  >
                    WhatsApp
                  </Typography.Text>
                  <Typography.Text strong className="reminder-modal-info-value">
                    {userMobile}
                  </Typography.Text>
                </div>
              </div>
            )}
          </div>

          <div className="reminder-modal-picker-section">
            <Typography.Text
              type="secondary"
              className="reminder-modal-info-label"
            >
              Choose reminder date &amp; time
            </Typography.Text>
            <DatePicker
              showNow={false}
              showTime={{ format: "hh:mm A", use12Hours: true }}
              format="D MMM YYYY, hh:mm A"
              value={reminderDate}
              onChange={(val) => setReminderDate(val)}
              minDate={today}
              maxDate={expiryDayjs.subtract(1, "day")}
              disabledDate={disabledDate}
              placeholder="Select date and time"
              style={{ width: "100%", marginTop: 6 }}
              size="large"
            />
          </div>

          <div className="reminder-modal-note">
            <BellOutlined className="reminder-modal-note-icon" />
            <Typography.Text className="reminder-modal-note-text">
              You can always come back and start the assessment before the
              deadline.
            </Typography.Text>
          </div>
        </div>

        <Divider style={{ margin: 0 }} />
        <div className="reminder-modal-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            icon={<BellOutlined />}
            disabled={!reminderDate}
            loading={loading}
            onClick={handleSubmit}
          >
            {mode === "edit" ? "Update Reminder" : "Set Reminder"}
          </Button>
        </div>
      </section>
    </Modal>
  );
};

export default ReminderModal;
